import { useState } from "react";
import { ApiError, extractIntakeImage } from "../../api";
import type { ExtractionResult } from "../../types/intake";

interface Props {
  onExtracted: (r: ExtractionResult) => void;
  onCancel: () => void;
  onDirty?: () => void;
  noteVariant?: "intake" | "call";
}

/** Claude accepts these directly; HEIC is converted client-side first. */
const SUPPORTED = ["image/jpeg", "image/png", "image/gif", "image/webp"];
/** Cap the long edge — keeps handwritten notes legible while staying under
 *  Anthropic's per-image size limit (large iPhone photos otherwise overflow). */
const MAX_EDGE = 2000;

function isHeic(file: File): boolean {
  const t = file.type.toLowerCase();
  return t === "image/heic" || t === "image/heif" || /\.(heic|heif)$/i.test(file.name);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("read_failed"));
    r.readAsDataURL(blob);
  });
}

/** Downscale to MAX_EDGE if larger; returns a JPEG blob, or null if no resize needed. */
function downscale(blob: Blob, maxEdge: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const longest = Math.max(img.width, img.height);
      if (longest <= maxEdge) {
        URL.revokeObjectURL(url);
        resolve(null);
        return;
      }
      const scale = maxEdge / longest;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (b) => {
          URL.revokeObjectURL(url);
          resolve(b);
        },
        "image/jpeg",
        0.85,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/** HEIC → JPEG (lazy-loaded heic2any), unsupported → throw, then downscale. */
async function prepareImage(file: File): Promise<{ dataUrl: string; mediaType: string }> {
  let blob: Blob = file;
  let mediaType = (file.type || "").toLowerCase();
  if (isHeic(file)) {
    const heic2any = (await import("heic2any")).default;
    const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.85 });
    blob = Array.isArray(out) ? out[0] : out;
    mediaType = "image/jpeg";
  } else if (!SUPPORTED.includes(mediaType)) {
    throw new Error(`unsupported:${mediaType || "unknown"}`);
  }
  const resized = await downscale(blob, MAX_EDGE);
  if (resized) {
    blob = resized;
    mediaType = "image/jpeg";
  }
  return { dataUrl: await blobToDataUrl(blob), mediaType };
}

/**
 * PhotoCapture — pick a photo of handwritten notes / a filled intake sheet.
 * iPhone photos (HEIC) are converted to JPEG in-browser before extraction, so
 * desktop file-pick works without a camera. Image → extract-image → Review.
 */
export function PhotoCapture({ onExtracted, onCancel, onDirty, noteVariant = "intake" }: Props) {
  const [preview, setPreview] = useState<string | null>(null);
  const [base64, setBase64] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState("image/jpeg");
  const [preparing, setPreparing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    onDirty?.();
    setError(null);
    setPreview(null);
    setBase64(null);
    setPreparing(true);
    try {
      const { dataUrl, mediaType: mt } = await prepareImage(file);
      setPreview(dataUrl);
      setBase64(dataUrl.split(",")[1] ?? "");
      setMediaType(mt);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "read_failed";
      setError(
        msg.startsWith("unsupported:")
          ? `Unsupported image type (${msg.slice(12)}). Use JPG, PNG, or HEIC.`
          : "Couldn't read that image — try a different file.",
      );
    } finally {
      setPreparing(false);
    }
  }

  async function extract() {
    if (!base64) return;
    setExtracting(true);
    setError(null);
    try {
      const result = await extractIntakeImage(base64, mediaType, noteVariant);
      onExtracted(result);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.code} (HTTP ${err.status})` : "extract_failed");
      setExtracting(false);
    }
  }

  return (
    <div className="intake-photo">
      {!preview ? (
        <label className="intake-photo-pick">
          <input
            type="file"
            accept="image/*,.heic,.heif"
            onChange={onPick}
            disabled={preparing}
            hidden
          />
          {preparing ? (
            <span className="intake-photo-prep">
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              {" "}reading image…
            </span>
          ) : (
            <>
              <span className="intake-photo-icon">📷</span>
              <span className="intake-photo-cta">Choose a photo of the notes</span>
              <span className="intake-photo-sub">JPG, PNG, or HEIC (iPhone photos OK)</span>
            </>
          )}
        </label>
      ) : (
        <div className="intake-photo-preview">
          <img src={preview} alt="intake photo preview" />
          <label className="intake-photo-redo">
            <input type="file" accept="image/*,.heic,.heif" onChange={onPick} hidden />
            Choose a different photo
          </label>
        </div>
      )}

      {error && <div className="write-confirm-error">⚠ {error}</div>}

      <div className="intake-actions">
        <button className="write-confirm-cancel" onClick={onCancel} disabled={extracting}>
          CANCEL
        </button>
        <button
          className="intake-chat-fill"
          onClick={() => void extract()}
          disabled={!base64 || preparing || extracting}
        >
          {extracting ? "EXTRACTING…" : "✦ EXTRACT & CONTINUE"}
        </button>
      </div>
    </div>
  );
}
